import axios from 'axios';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDate, usePermissions } from '@/composables';
import { useErrorStore } from './errors';

export const useScheduleStore = defineStore('schedule', () => {
  const {
    toUniversalDate,
    getAsDate,
  } = useDate();
  const { showSnackMessage } = useErrorStore();
  const { can } = usePermissions();
  const { t } = useI18n();

  const selectedDate = ref<string>(sessionStorage.getItem('scheduleDate') ?? toUniversalDate(getAsDate()));
  const weekView = ref(false);
  const readOnly = computed(() => !can('manage_shifts'));
  const dayShiftPlan = ref<DayShiftPlan>([]);
  const timeBands = ref<any>();
  const loadingWeekView = ref(true);
  const loadingDayView = ref(true);
  const lastLoadedDate = ref();
  const loading = ref(false);
  const publishers = ref<App.Models.User[]>([]);
  const publisherIndex = ref<Map<number, App.Models.User>>(new Map());

  function doesUserHaveDependentLinkedPublisher(
    shift: App.ApiResources.ShiftResource | null,
    publisher: App.Models.User,
  ) {
    if (shift === null || shift === undefined || publisher === null || publisher === undefined) {
      return [];
    }
    if (publisher.child_users.length === 0) {
      return [];
    }

    const childUserIds = publisher.child_users.map((child) => child.id);
    const childUsersOnShift = shift.assigned_publishers.filter((assigned_publisher) => {
      return childUserIds.includes(assigned_publisher.id);
    });

    if (childUsersOnShift.length === 0) {
      return [];
    }

    const childUsersThatWillBeRemoved = [];
    childUsersOnShift.forEach(child => {
      const childsParentUsersOnShift = shift.assigned_publishers.filter((pub) => {
        return pub.child_users.map((a) => a.id).includes(child.id);
      });

      if (childsParentUsersOnShift.length <= 1) {
        childUsersThatWillBeRemoved.push(child);
      }
    });
    return childUsersThatWillBeRemoved;
  }

  function hasDayShiftPlan(): boolean {
    return Array.isArray(dayShiftPlan.value) && dayShiftPlan.value.length > 0;
  }

  function bandsOverlap(start1, end1, start2, end2) {
    return start1 < end2 && start2 < end1;
  }

  function isPublisherAssignedInTimeslot(
    shift: App.ApiResources.ShiftResource,
    publisher: App.Models.User,
  ) {
    let found = false;
    let loc = null;

    if (!shift || !publisher || !hasDayShiftPlan()) {
      return {
        adjacent: false,
        location: null,
      };
    }

    const sameTimeslotShifts = dayShiftPlan.value.filter((shft) =>
      // Do we need to change this?
      bandsOverlap(shft.begins, shft.ends, shift.begins, shift.ends),
    );

    sameTimeslotShifts.forEach((shft) => {
      shft.locations.forEach((location) => {
        if (location.shift.assigned_publishers.some((pub) => pub.id === publisher.id)) {
          found = true;
          loc = location;
        }
      });
    });

    return {
      found,
      location: loc,
    };
  }

  function doesUserHaveAdjacentShift(
    shift: App.ApiResources.ShiftResource,
    publisher: any,
  ) {
    if (!shift || !publisher || !hasDayShiftPlan()) {
      return {
        adjacent: false,
        location: null,
      };
    }

    const adjacentShifts = dayShiftPlan.value.filter(shiftPlan =>
      shiftPlan.ends === shift.begins || shiftPlan.begins === shift.ends,
    );

    for (const adjacentShift of adjacentShifts ?? []) {
      for (const location of adjacentShift.locations) {
        if (location.shift.assigned_publishers.some(pub => pub.id === publisher.id)) {
          return { found: true, location };
        }
      }
    }

    return {
      found: false,
      location: null,
    };
  };

  function doesUserHaveAdjacentAvailability(
    shift: App.ApiResources.ShiftResource,
    publisher: any,
  ): boolean {
    if (!shift || !publisher || !hasDayShiftPlan()) {
      return false;
    }

    const adjacentShifts = dayShiftPlan.value.filter(shiftPlan =>
      shiftPlan.ends === shift.begins || shiftPlan.begins === shift.ends,
    );

    for (const adjacentShift of adjacentShifts ?? []) {
      for (const location of adjacentShift.locations) {
        if (location.shift.available_publishers?.some(pub => pub.id === publisher.id)) {
          return true;
        }
      }
    }

    return false;
  };

  function appendDetails(publisher) {
    const extendedPub = publisherIndex.value.get(publisher.user_id);
    if (extendedPub === null || extendedPub === undefined || publisher === null || publisher === undefined) {
      return publisher;
    }
    return { ...publisher, ...extendedPub, id: publisher.user_id };
  }

  function indexPublishers(list: App.Models.User[]): Map<number, App.Models.User> {
    return new Map(list.map(publisher => [publisher.id, publisher]));
  }

  async function pollDayShiftPlan() {
    await loadDayShiftPlan({ force: false, update: true });
  }

  async function getShiftPlanUpdates() {
    await loadDayShiftPlan({ force: true, update: true });
  }

  /**
   * Loads the day shift plan.
   *
   * @param options - The options for loading the day shift plan.
   * @param options.force - If true, forces a new request even if a request is already in progress. Defaults to true.
   * @param options.update - If true, makes a request that only refreshes data that changed after the last request. Defaults to false.
   * @param options.includeMarkers - If true, includes location markers in the response (used for map views). Defaults to false.
   */
  async function loadDayShiftPlan(options: { force?: boolean; update?: boolean; includeMarkers?: boolean }) {
    const force = options.force ?? true;
    const update = options.update ?? false;
    const includeMarkers = options.includeMarkers ?? false;
    try {
      if (!loading.value || force) {
        loading.value = true;
        sessionStorage.setItem('scheduleDate', selectedDate.value);
        const response = await axios.get<{ data: { publishers: App.Models.User[]; slots: App.ApiResources.ShiftPlanSlotResource[]; bands: App.Models.ShiftTimeBand[]; date: string; updated_since: Date } }>(`/api/shifts/schedule/day?date=${selectedDate.value}&updated_since=${update ? lastLoadedDate.value : ''}&include_markers=${includeMarkers ? '1' : '0'}`);

        const incomingPublishers = response.data.data.publishers;
        if (update) {
          const merged = new Map(publisherIndex.value);
          incomingPublishers.forEach((publisher) => {
            merged.set(publisher.id, publisher);
          });
          publisherIndex.value = merged;
          publishers.value = Array.from(merged.values());
        }
        else {
          publishers.value = incomingPublishers;
          publisherIndex.value = indexPublishers(incomingPublishers);
        }

        const dsp = response.data.data.slots;

        dsp.forEach((slot, slotIndex) => {
          slot.locations.forEach((location, locationIndex) => {
            if (location.shift.captain != null) {
              location.shift.captain = appendDetails(location.shift.captain);
            }
            location.shift.assigned_brothers.forEach((publisher, publisherIndex) => {
              dsp[slotIndex].locations[locationIndex].shift.assigned_brothers[publisherIndex] = appendDetails(publisher);
            });
            location.shift.assigned_elsewhere_publishers.forEach((publisher, publisherIndex) => {
              dsp[slotIndex].locations[locationIndex].shift.assigned_elsewhere_publishers[publisherIndex] = appendDetails(publisher);
            });
            location.shift.assigned_publishers.forEach((publisher, publisherIndex) => {
              dsp[slotIndex].locations[locationIndex].shift.assigned_publishers[publisherIndex] = appendDetails(publisher);
            });
            location.shift.available_captains.forEach((publisher, publisherIndex) => {
              dsp[slotIndex].locations[locationIndex].shift.available_captains[publisherIndex] = appendDetails(publisher);
            });
            location.shift.available_publishers.forEach((publisher, publisherIndex) => {
              dsp[slotIndex].locations[locationIndex].shift.available_publishers[publisherIndex] = appendDetails(publisher);
            });
            location.shift.cancelled_publishers.forEach((publisher, publisherIndex) => {
              dsp[slotIndex].locations[locationIndex].shift.cancelled_publishers[publisherIndex] = appendDetails(publisher);
            });
            location.shift.limit_reached_publishers.forEach((publisher, publisherIndex) => {
              dsp[slotIndex].locations[locationIndex].shift.limit_reached_publishers[publisherIndex] = appendDetails(publisher);
            });
            location.shift.publishers.forEach((publisher, publisherIndex) => {
              dsp[slotIndex].locations[locationIndex].shift.publishers[publisherIndex] = appendDetails(publisher);
            });
            location.shift.adjacent_publishers.forEach((publisher, publisherIndex) => {
              dsp[slotIndex].locations[locationIndex].shift.adjacent_publishers[publisherIndex] = appendDetails(publisher);
            });

            if (update) {
              dayShiftPlan.value[slotIndex].locations.find((l) => l.id === location.id).shift = location.shift;
            }
          });
        });

        if (!update) {
          dayShiftPlan.value = dsp;
          timeBands.value = response.data.data.bands;
        }

        lastLoadedDate.value = response.data.data.updated_since;
        loading.value = false;
      }
    }
    catch (error) {
      if (!axios.isCancel(error)) {
        console.error(error);
        showSnackMessage(`${t('shift.detail.cannot_load')}: ${selectedDate.value}`);
        loading.value = false;
      }
    }
  }

  return {
    loadDayShiftPlan,
    pollDayShiftPlan,
    bandsOverlap,
    getShiftPlanUpdates,
    doesUserHaveAdjacentShift,
    doesUserHaveAdjacentAvailability,
    isPublisherAssignedInTimeslot,
    doesUserHaveDependentLinkedPublisher,
    dayShiftPlan,
    loadingDayView,
    loadingWeekView,
    selectedDate,
    weekView,
    publishers,
    readOnly,
    lastLoadedDate,
  };
});
